import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { FactEvidenceSchema, FactOriginSchema, FactStatusSchema } from './fact-provenance.schemas';

export const VALUATION_AGENT_FINDINGS_MAX = 200;

export const ValuationAgentFindingSchema = z
  .object({
    factKey: z.string().trim().min(1).max(120),
    value: z.json().nullable(),
    occurrence: z.string().nullable().optional(),
    // Optional on the wire on purpose: a missing one must reach the per-finding refusal, not fail the whole call.
    origin: FactOriginSchema.nullable().optional(),
    status: FactStatusSchema.nullable().optional(),
    evidence: FactEvidenceSchema.nullable().optional(),
    timeReference: z.string().nullable().optional(),
    reason: z.string().nullable().optional(),
  })
  .strict();

export const WriteValuationAgentFindingsBodySchema = z
  .object({ findings: z.array(ValuationAgentFindingSchema).min(1).max(VALUATION_AGENT_FINDINGS_MAX) })
  .strict()
  .refine(
    ({ findings }) =>
      new Set(findings.map((finding) => `${finding.factKey}\u0000${finding.occurrence?.trim() ?? ''}`)).size ===
      findings.length,
    { message: 'each (factKey, occurrence) at most once', path: ['findings'] },
  );

export const ValuationAgentFindingResultSchema = z.discriminatedUnion('outcome', [
  z.object({
    index: z.number().int().nonnegative(),
    factKey: z.string(),
    outcome: z.literal('STORED'),
    factId: z.uuid(),
  }),
  z.object({
    index: z.number().int().nonnegative(),
    factKey: z.string(),
    outcome: z.literal('REFUSED'),
    code: z.string(),
    details: z.record(z.string(), z.unknown()),
  }),
]);

export const WriteValuationAgentFindingsResponseSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  results: z.array(ValuationAgentFindingResultSchema),
});

export type ValuationAgentFinding = z.infer<typeof ValuationAgentFindingSchema>;
export type WriteValuationAgentFindingsBody = z.infer<typeof WriteValuationAgentFindingsBodySchema>;
export type ValuationAgentFindingResult = z.infer<typeof ValuationAgentFindingResultSchema>;
export type WriteValuationAgentFindingsResponse = z.infer<typeof WriteValuationAgentFindingsResponseSchema>;
